import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Calendar } from "lucide-react";
import { BLOG_POSTS } from "../data/content";

function renderInline(text) {
  return text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((part, j) => {
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={j}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*")) return <em key={j}>{part.slice(1, -1)}</em>;
    return part;
  });
}

function renderContent(text) {
  return text.split("\n\n").map((block, i) => {
    if (block.startsWith("## ")) {
      return (
        <h2 key={i} className="font-mono font-bold text-xl text-gray-800 mt-8 mb-3">
          {block.replace("## ", "")}
        </h2>
      );
    }
    if (block.startsWith("> ")) {
      return (
        <blockquote key={i} className="border-l-4 border-sky-light bg-sky-pastel/40 rounded-r-2xl px-5 py-3 my-4 font-sans text-gray-600 text-sm italic">
          {renderInline(block.replace(/^> /, ""))}
        </blockquote>
      );
    }
    if (block.startsWith("- ") || block.startsWith("**- ")) {
      const items = block.split("\n").filter(l => l.trim());
      return (
        <ul key={i} className="font-sans text-gray-700 text-base leading-relaxed mb-4 space-y-1 list-none pl-0">
          {items.map((item, j) => (
            <li key={j} className="flex items-start gap-2">
              <span className="text-mint-medium mt-0.5 flex-shrink-0">•</span>
              <span>{renderInline(item.replace(/^[-*]\s*/, ""))}</span>
            </li>
          ))}
        </ul>
      );
    }
    if (block.startsWith("[img:")) {
      const match = block.match(/\[img:(.*?)\|(.*?)\]/);
      if (match) {
        return (
          <figure key={i} className="my-8">
            <img
              src={match[1]}
              alt={match[2]}
              className="w-full rounded-2xl shadow-md object-cover"
              style={{ maxHeight: "400px" }}
            />
            <figcaption className="text-center font-sans text-xs text-gray-400 mt-2 italic">{match[2]}</figcaption>
          </figure>
        );
      }
    }
    if (block.startsWith("[callout:")) {
      const match = block.match(/\[callout:(.*?)\]([\s\S]*)/);
      if (match) {
        const colors = {
          blue: "bg-sky-pastel/60 border-sky-light text-blue-700",
          green: "bg-mint-pastel/60 border-mint-light text-green-700",
          amber: "bg-amber-50 border-amber-200 text-amber-800",
          purple: "bg-purple-50 border-purple-200 text-purple-700",
        };
        const cls = colors[match[1]] || colors.blue;
        return (
          <div key={i} className={`rounded-2xl border-2 px-5 py-4 my-6 font-sans text-sm leading-relaxed ${cls}`}>
            {renderInline(match[2].trim())}
          </div>
        );
      }
    }
    return (
      <p key={i} className="font-sans text-gray-700 text-base leading-relaxed mb-4">
        {renderInline(block)}
      </p>
    );
  });
}

export default function BlogPost() {
  const { id } = useParams();
  const post = BLOG_POSTS.find((p) => p.id === Number(id));

  if (!post) return <Navigate to="/blog" replace />;

  const currentIndex = BLOG_POSTS.findIndex((p) => p.id === post.id);
  const prev = BLOG_POSTS[currentIndex + 1] || null;
  const next = BLOG_POSTS[currentIndex - 1] || null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">

      <Link to="/blog" className="inline-flex items-center gap-2 font-mono text-sm text-gray-500 hover:text-gray-800 transition-colors mb-8">
        <ArrowLeft size={16} /> Quay lại Blog
      </Link>

      {post.thumbnail ? (
        <div className="w-full h-52 rounded-3xl overflow-hidden mb-8">
          <img src={post.thumbnail} alt={post.title} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className={`w-full h-52 rounded-3xl bg-gradient-to-br ${post.thumbnailBg} flex items-center justify-center text-7xl mb-8`}>
          {post.thumbnailEmoji}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <span className="tag">{post.category}</span>
        {post.tags.slice(1).map((t) => (
          <span key={t} className="tag">{t}</span>
        ))}
      </div>

      <h1 className="font-sans font-bold text-2xl md:text-3xl text-gray-900 leading-snug mb-4">
        {post.title}
      </h1>

      <div className="flex items-center gap-4 text-gray-400 text-xs font-sans mb-8 pb-8 border-b border-gray-400">
        <span className="flex items-center gap-1"><Calendar size={12} />{post.date}</span>
      </div>

      <div className="prose-custom">
        {renderContent(post.content)}
      </div>

      <div className="mt-10 bg-sky-pastel/60 rounded-2xl p-5 border border-sky-light">
        <p className="font-sans text-gray-600 text-sm leading-relaxed">
          <span className="font-bold text-blue-600 font-mono">💡 Ảnh hưởng gì đến tụi mình?</span><br />
          {post.impact}
        </p>
      </div>

      <div className="mt-12 grid grid-cols-2 gap-4">
        {next && (
          <Link to={`/blog/${next.id}`} className="card hover:-translate-y-1 transition-all duration-300 col-start-1 p-5">
            <p className="font-sans text-xs text-gray-400 mb-2">← Bài trước</p>
            <p className="font-sans font-semibold text-sm text-gray-700 line-clamp-2 leading-relaxed">{next.title}</p>
          </Link>
        )}
        {prev && (
          <Link to={`/blog/${prev.id}`} className="card hover:-translate-y-1 transition-all duration-300 col-start-2 text-right p-5">
            <p className="font-sans text-xs text-gray-400 mb-2">Bài sau →</p>
            <p className="font-sans font-semibold text-sm text-gray-700 line-clamp-2 leading-relaxed">{prev.title}</p>
          </Link>
        )}
      </div>

    </div>
  );
}
